import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

const slides = [
  {
    src: 'https://public.readdy.ai/ai/img_res/54541d847614881130bda75a14c635ef.jpg',
    label: 'Premium Outdoor Living Spaces',
  },
  {
    src: 'https://readdy.ai/api/search-image?query=stunning%20outdoor%20landscape%20lighting%20at%20night%20luxury%20Florida%20home%20pathway%20uplighting%20palm%20trees%20warm%20amber%20glow%20premium%20garden%20design%20elegant%20residence%20dusk%20moody%20cinematic%20atmosphere%20high-end%20outdoor%20lighting&width=1920&height=1080&seq=dippels-hero-2&orientation=landscape',
    label: 'Architectural Landscape Lighting',
  },
  {
    src: 'https://readdy.ai/api/search-image?query=premium%20custom%20paver%20driveway%20and%20walkway%20luxury%20Florida%20residential%20property%20pristine%20manicured%20lawn%20lush%20green%20tropical%20landscaping%20professional%20craftsmanship%20elegant%20curb%20appeal%20upscale%20neighborhood%20rich%20stone%20pavers&width=1920&height=1080&seq=dippels-hero-3&orientation=landscape',
    label: 'Master Paver Craftsmanship',
  },
];

export default function HeroSection() {
  const [current, setCurrent] = useState(0);
  const [animating, setAnimating] = useState(false);

  useEffect(() => {
    const timer = setInterval(() => {
      setAnimating(true);
      setTimeout(() => {
        setCurrent((p) => (p + 1) % slides.length);
        setAnimating(false);
      }, 600);
    }, 6000);
    return () => clearInterval(timer);
  }, []);

  const goTo = (i: number) => {
    setAnimating(true);
    setTimeout(() => {
      setCurrent(i);
      setAnimating(false);
    }, 300);
  };

  return (
    <section className="relative h-screen min-h-[750px] w-full overflow-hidden">
      {/* Slides */}
      {slides.map((slide, i) => (
        <div
          key={i}
          className="absolute inset-0 transition-opacity duration-[1400ms] ease-in-out"
          style={{ opacity: i === current ? 1 : 0 }}
        >
          <img
            src={slide.src}
            alt={`${slide.label} — Dippel's Outdoor Services Florida`}
            className="w-full h-full object-cover object-center"
          />
        </div>
      ))}

      {/* Cinematic overlays */}
      <div className="absolute inset-0 bg-gradient-to-r from-black/75 via-black/45 to-black/20"></div>
      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/20"></div>

      {/* Decorative vertical accent */}
      <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-transparent via-gold to-transparent opacity-60"></div>

      {/* Content */}
      <div className="relative z-10 h-full flex items-center">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 w-full">
          <div className="max-w-[680px]">

            {/* Eyebrow label */}
            <div className="flex items-center gap-3 mb-7 hero-fade-1">
              <div className="h-px w-10 bg-gold"></div>
              <span className="text-gold font-sans text-[11px] font-semibold tracking-[0.28em] uppercase">
                Florida&apos;s Premier Outdoor Design &amp; Build
              </span>
            </div>

            {/* H1 */}
            <h1 className="font-serif text-white font-bold leading-[1.07] mb-7 hero-fade-2" style={{ fontSize: 'clamp(42px, 5.5vw, 78px)' }}>
              Outdoor Living,
              <br />
              <em className="text-gold not-italic">Elevated</em> to an
              <br />
              Art Form.
            </h1>

            <p className="text-white/70 font-sans leading-relaxed mb-10 hero-fade-3" style={{ fontSize: 'clamp(15px, 1.4vw, 18px)', maxWidth: '520px' }}>
              Dippel's Outdoor Services crafts premium fences, pavers, landscapes, lighting systems &amp; custom sheds across Palm Coast, Ormond Beach, Daytona Beach &amp; the Florida east coast.
            </p>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-4 hero-fade-4">
              <Link
                to="/contact"
                className="group inline-flex items-center justify-center gap-2.5 bg-gold text-charcoal font-bold text-[15px] px-9 py-4.5 rounded-full hover:bg-gold-light transition-all whitespace-nowrap"
                style={{ padding: '1.1rem 2.25rem' }}
              >
                <i className="ri-pencil-ruler-2-line text-base"></i>
                Request a Design Consultation
              </Link>
              <a
                href="tel:3863014399"
                className="inline-flex items-center justify-center gap-2.5 border border-white/40 text-white font-semibold text-[15px] rounded-full hover:border-white/80 hover:bg-white/8 transition-all whitespace-nowrap"
                style={{ padding: '1.1rem 2.25rem' }}
              >
                <i className="ri-phone-fill text-gold"></i>
                (386) 301-4399
              </a>
            </div>

            {/* Trust indicators */}
            <div className="flex flex-wrap items-center gap-x-7 gap-y-3 mt-10 pt-8 border-t border-white/15 hero-fade-5">
              <div className="flex items-center gap-2.5">
                <div className="flex gap-0.5">
                  {[...Array(5)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-gold text-xs"></i>
                  ))}
                </div>
                <span className="text-white/65 text-[13px] font-medium">5-Star Rated on Google</span>
              </div>
              <div className="w-px h-4 bg-white/20 hidden sm:block"></div>
              <div className="flex items-center gap-2">
                <i className="ri-map-pin-2-fill text-gold text-sm"></i>
                <span className="text-white/65 text-[13px]">Volusia &amp; Flagler County, FL</span>
              </div>
              <div className="w-px h-4 bg-white/20 hidden sm:block"></div>
              <div className="flex items-center gap-2">
                <i className="ri-shield-check-fill text-gold text-sm"></i>
                <span className="text-white/65 text-[13px]">Licensed &amp; Insured</span>
              </div>
              <div className="w-px h-4 bg-white/20 hidden sm:block"></div>
              <div className="flex items-center gap-2">
                <i className="ri-graduation-cap-fill text-gold text-sm"></i>
                <span className="text-white/65 text-[13px]">Degree-Trained Experts</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Slide dots */}
      <div className="absolute bottom-10 left-1/2 -translate-x-1/2 flex gap-2 z-10">
        {slides.map((_, i) => (
          <button
            key={i}
            onClick={() => goTo(i)}
            className={`rounded-full transition-all duration-400 cursor-pointer ${
              i === current ? 'w-9 h-2 bg-gold' : 'w-2 h-2 bg-white/30 hover:bg-white/50'
            }`}
            aria-label={`Slide ${i + 1}`}
          />
        ))}
      </div>

      {/* Slide label */}
      <div className="absolute bottom-10 right-10 hidden lg:flex items-center gap-2 z-10">
        <span
          className={`text-white/40 text-[11px] tracking-[0.22em] uppercase font-sans transition-opacity duration-300 ${animating ? 'opacity-0' : 'opacity-100'}`}
        >
          {slides[current].label}
        </span>
      </div>

      {/* Scroll cue */}
      <div className="absolute bottom-10 right-6 lg:right-auto lg:left-12 flex flex-col items-center gap-2 z-10 hidden lg:flex">
        <div className="w-px h-14 bg-gradient-to-b from-white/40 to-transparent"></div>
        <span className="text-white/35 text-[10px] tracking-[0.25em] uppercase" style={{ writingMode: 'vertical-rl' }}>Scroll</span>
      </div>
    </section>
  );
}
